import { Seo } from "../components/Seo";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Gift, Star, Smartphone, ArrowRight, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { PageHeader } from "../components/PageHeader";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

export default function Rewards() {
  const [rewards, setRewards] = useState([]);
  const { user } = useAuth();

  useEffect(() => {
    api.get("/rewards").then((r) => setRewards(r.data)).catch(() => {});
  }, []);

  const steps = [
    [Star, "Earn points", "Collect points on every visit, treatment and membership month."],
    [Gift, "Redeem rewards", "Trade points for facials, credits and exclusive perks."],
    [Sparkles, "Enjoy perks", "Unlock members-only events and seasonal surprises."],
  ];

  return (
    <div data-testid="rewards-page">
      <Seo title="Rewards Program" description="Earn points on every visit and redeem them for treatments and products at Iridescent Medical & Wellness Spa in Clinton Township, MI." path="/rewards" />
      <PageHeader
        overline="Iridescent Rewards"
        title="Every glow earns rewards"
        subtitle="Our loyalty program turns your self-care routine into complimentary treatments, credits and VIP perks."
      >
        <div className="mt-8 flex flex-wrap gap-4">
          {user ? (
            <Link to="/dashboard">
              <Button data-testid="rewards-dashboard-button" className="iri-gradient text-ink rounded-full h-12 px-8 font-medium hover:scale-105 transition-transform">
                View my points ({user.points})
              </Button>
            </Link>
          ) : (
            <Link to="/register">
              <Button data-testid="rewards-join-button" className="iri-gradient text-ink rounded-full h-12 px-8 font-medium hover:scale-105 transition-transform">
                Join & get 100 points
              </Button>
            </Link>
          )}
        </div>
      </PageHeader>

      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Stagger className="grid md:grid-cols-3 gap-6">
            {steps.map(([Icon, title, sub], i) => (
              <StaggerItem key={title}>
                <div className="bg-white rounded-2xl p-8 border border-slate-100 h-full">
                  <span className="h-12 w-12 rounded-full iri-gradient flex items-center justify-center">
                    <Icon className="h-5 w-5 text-ink" strokeWidth={1.5} />
                  </span>
                  <p className="text-xs text-[#8A9492] mt-5">Step {i + 1}</p>
                  <h3 className="font-heading text-xl font-medium text-ink mt-1">{title}</h3>
                  <p className="text-sm text-[#5C6664] mt-2 leading-relaxed">{sub}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <h2 className="font-heading text-2xl md:text-3xl font-light text-ink mb-8">Rewards catalog</h2>
          </Reveal>
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {rewards.map((r) => (
              <StaggerItem key={r.id}>
                <div className="bg-white rounded-2xl p-6 border border-slate-100 hover-lift h-full flex flex-col" data-testid={`reward-item-${r.id}`}>
                  <div className="flex items-center justify-between">
                    <Gift className="h-6 w-6 text-iri-mid" strokeWidth={1.5} />
                    <span className="iri-gradient text-ink text-sm font-medium px-3 py-1 rounded-full">{r.cost} pts</span>
                  </div>
                  <h3 className="font-heading text-lg font-medium text-ink mt-4">{r.name}</h3>
                  <p className="text-sm text-[#5C6664] mt-2 flex-1">{r.description}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal className="bg-ink rounded-[2rem] p-8 md:p-14 flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-light text-white">Take rewards on the go</h2>
              <p className="text-white/60 mt-3 max-w-md">Track points and book from the Boulevard client app.</p>
            </div>
            <div className="flex gap-3">
              <a href="https://apps.apple.com" target="_blank" rel="noopener noreferrer" data-testid="app-store-link">
                <Button className="bg-white text-ink rounded-full h-12 px-6 gap-2 hover:bg-white/90">
                  <Smartphone className="h-4 w-4" /> App Store
                </Button>
              </a>
              <a href="https://play.google.com" target="_blank" rel="noopener noreferrer" data-testid="google-play-link">
                <Button variant="outline" className="rounded-full h-12 px-6 gap-2 border-white/30 text-white bg-transparent hover:bg-white/10">
                  <Smartphone className="h-4 w-4" /> Google Play
                </Button>
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
